/**
 * Activity Tracking Hook
 * Automatically tracks page views and provides methods for tracking other activities
 */
import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useActivityTracking = () => {
  const location = useLocation();
  const { token } = useAuth();
  const lastPageRef = useRef(null);
  const pageEnterTimeRef = useRef(Date.now());

  // Track page view
  const trackPageView = useCallback(async (page, pageTitle = "", referrer = "") => {
    if (!token) return;
    
    const timeOnPreviousPage = lastPageRef.current 
      ? Math.round((Date.now() - pageEnterTimeRef.current) / 1000)
      : 0;

    try {
      await fetch(`${BACKEND_URL}/api/logs/page-view`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          page,
          page_title: pageTitle,
          referrer: referrer || lastPageRef.current || "",
          time_on_page: timeOnPreviousPage
        })
      });
    } catch (error) {
      console.error("Failed to track page view:", error);
    }

    lastPageRef.current = page;
    pageEnterTimeRef.current = Date.now();
  }, [token]);

  // Track data access
  const trackDataAccess = useCallback(async (dataType, recordId = "", recordDetails = "", accessType = "view") => {
    if (!token) return;

    try {
      await fetch(`${BACKEND_URL}/api/logs/data-access`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          data_type: dataType,
          record_id: recordId,
          record_details: recordDetails,
          access_type: accessType
        })
      });
    } catch (error) {
      console.error("Failed to track data access:", error);
    }
  }, [token]);

  // Track data export
  const trackExport = useCallback(async (exportType, module, recordCount = 0, filtersApplied = {}) => {
    if (!token) return;

    try {
      await fetch(`${BACKEND_URL}/api/logs/export`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          export_type: exportType,
          module,
          record_count: recordCount,
          filters_applied: filtersApplied
        })
      });
    } catch (error) {
      console.error("Failed to track export:", error);
    }
  }, [token]);

  // Auto-track page views on route change
  useEffect(() => {
    const pageName = location.pathname === "/" ? "dashboard" : location.pathname;
    const pageTitle = document.title || pageName;
    trackPageView(pageName, pageTitle);
  }, [location.pathname, trackPageView]);

  return {
    trackPageView,
    trackDataAccess,
    trackExport
  };
};

export default useActivityTracking;
